import { useState } from 'react';
import { Link, useForm } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Components/PageHeader';
import SearchableSelect from '@/Components/SearchableSelect';

export default function DestinationsCreate({ countries }) {
    const [imagePreview, setImagePreview] = useState(null);

    const { data, setData, post, processing, errors } = useForm({
        name: '',
        country_id: '',
        description: '',
        image: null,
        sort_order: 0,
        is_active: true,
        is_featured: false,
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        post('/destinations', {
            forceFormData: true,
        });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setData('image', file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <AppLayout title="Add Destination">
            <PageHeader
                title="Add Destination"
                subtitle="Create a new travel destination"
                breadcrumbs={[
                    { label: 'Travel' },
                    { label: 'Destinations', href: '/destinations' },
                    { label: 'Create' },
                ]}
            />

            <div className="row">
                <div className="col-lg-8">
                    <form onSubmit={handleSubmit}>
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Destination Details</h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Destination Name <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                            value={data.name}
                                            onChange={(e) => setData('name', e.target.value)}
                                            placeholder="e.g., Dubai, Maldives, Paris"
                                        />
                                        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Country <span className="text-danger">*</span></label>
                                        <SearchableSelect
                                            options={countries}
                                            value={data.country_id}
                                            onChange={(value) => setData('country_id', value)}
                                            placeholder="Select Country"
                                            searchPlaceholder="Search countries..."
                                            error={!!errors.country_id}
                                        />
                                        {errors.country_id && <div className="invalid-feedback d-block">{errors.country_id}</div>}
                                    </div>

                                    <div className="col-12">
                                        <label className="form-label">Description</label>
                                        <textarea
                                            className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                                            rows="4"
                                            value={data.description}
                                            onChange={(e) => setData('description', e.target.value)}
                                            placeholder="Brief description of the destination..."
                                        ></textarea>
                                        {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Image</label>
                                        <input
                                            type="file"
                                            className={`form-control ${errors.image ? 'is-invalid' : ''}`}
                                            accept="image/*"
                                            onChange={handleImageChange}
                                        />
                                        {errors.image && <div className="invalid-feedback">{errors.image}</div>}
                                        <small className="text-muted">Max file size: 2MB. Supported: JPG, PNG, GIF</small>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Sort Order</label>
                                        <input
                                            type="number"
                                            className={`form-control ${errors.sort_order ? 'is-invalid' : ''}`}
                                            value={data.sort_order}
                                            onChange={(e) => setData('sort_order', parseInt(e.target.value) || 0)}
                                            min="0"
                                        />
                                        {errors.sort_order && <div className="invalid-feedback">{errors.sort_order}</div>}
                                        <small className="text-muted">Lower numbers appear first</small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Settings</h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <div className="form-check form-switch">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="is_active"
                                                checked={data.is_active}
                                                onChange={(e) => setData('is_active', e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="is_active">
                                                Active
                                            </label>
                                        </div>
                                        <small className="text-muted">Inactive destinations won't be shown in selections</small>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-check form-switch">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="is_featured"
                                                checked={data.is_featured}
                                                onChange={(e) => setData('is_featured', e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="is_featured">
                                                Featured
                                            </label>
                                        </div>
                                        <small className="text-muted">Featured destinations appear prominently</small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex gap-2">
                            <button type="submit" className="btn btn-primary" disabled={processing}>
                                {processing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-lg me-1"></i>
                                        Create Destination
                                    </>
                                )}
                            </button>
                            <Link href="/destinations" className="btn btn-outline-secondary">
                                Cancel
                            </Link>
                        </div>
                    </form>
                </div>

                <div className="col-lg-4">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Preview</h5>
                        </div>
                        <div className="card-body">
                            {imagePreview ? (
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    className="img-fluid rounded mb-3"
                                    style={{ maxHeight: 200, width: '100%', objectFit: 'cover' }}
                                />
                            ) : (
                                <div
                                    className="rounded mb-3 d-flex align-items-center justify-content-center"
                                    style={{ height: 200, background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)' }}
                                >
                                    <i className="bi bi-image text-white" style={{ fontSize: '3rem' }}></i>
                                </div>
                            )}
                            <h5 className="mb-1">{data.name || 'Destination Name'}</h5>
                            <p className="text-muted small mb-2">
                                {countries.find(c => c.id == data.country_id)?.name || 'Country'}
                            </p>
                            <p className="small text-muted mb-0">
                                {data.description || 'No description provided'}
                            </p>
                            <div className="mt-3">
                                {data.is_featured && (
                                    <span className="badge bg-warning text-dark me-2">
                                        <i className="bi bi-star-fill me-1"></i>
                                        Featured
                                    </span>
                                )}
                                <span className={`badge ${data.is_active ? 'badge-success' : 'badge-secondary'}`}>
                                    {data.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}
